"use strict";
exports.__esModule = true;
var tslib_1 = require("tslib");
// (C) 2007-2020 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
var mapbox_gl_1 = tslib_1.__importDefault(require("mapbox-gl"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var geoChartDataLayers_1 = require("./geoChartDataLayers");
var geoChartDataSource_1 = require("./geoChartDataSource");
var geoChart_1 = require("./constants/geoChart");
var geoChartTooltip_1 = require("./geoChartTooltip");
var viewport_1 = require("./helpers/geoChart/viewport");
var common_1 = require("./helpers/geoChart/common");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var drilling_1 = require("./helpers/geoChart/drilling");
var GeoChartRenderer = /** @class */ (function (_super) {
    tslib_1.__extends(GeoChartRenderer, _super);
    function GeoChartRenderer(props) {
        var _this = _super.call(this, props) || this;
        _this.setChartRef = function (ref) {
            _this.chartRef = ref;
        };
        _this.createMap = function () {
            var _a = _this.props, config = _a.config, geoData = _a.geoData;
            var data = geoData.location.data;
            var _b = (config || {}).isExportMode, isExportMode = _b === void 0 ? false : _b;
            var isViewportFrozen = _this.isViewportFrozen();
            _this.chart = new mapbox_gl_1["default"].Map(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, geoChart_1.DEFAULT_MAPBOX_OPTIONS), viewport_1.getViewportOptions(data, config)), { container: _this.chartRef, 
                // If true, the map’s canvas can be exported to a PNG using map.getCanvas().toDataURL().
                // This is false by default as a performance optimization.
                interactive: !isViewportFrozen, preserveDrawingBuffer: isExportMode }));
        };
        _this.updateMapWithConfig = function (prevConfig, prevColorStrategy) {
            if (_this.shouldResetMap(prevConfig, prevColorStrategy)) {
                // Config for clustering and pushpin size lead to change layer setting
                // Then calling resetMap here is needed
                _this.resetMap();
            }
            _this.updatePanAndZoom();
            _this.updateViewport(prevConfig);
        };
        _this.resetMap = function () {
            _this.cleanupMap();
            _this.setupMap();
        };
        _this.shouldResetMap = function (prevConfig, prevColorStrategy) {
            var _a = _this.props, colorStrategy = _a.colorStrategy, config = _a.config;
            if (common_1.isPointsConfigChanged(prevConfig.points, config.points)) {
                return true;
            }
            if (common_1.isColorAssignmentItemChanged(prevColorStrategy.getColorAssignment(), colorStrategy.getColorAssignment())) {
                return true;
            }
            return false;
        };
        _this.isViewportFrozen = function () {
            var _a, _b;
            var config = _this.props.config;
            return (_b = (_a = config === null || config === void 0 ? void 0 : config.viewport) === null || _a === void 0 ? void 0 : _a.frozen) !== null && _b !== void 0 ? _b : false;
        };
        _this.removeMapControls = function () {
            if (_this.navigationControlButton) {
                _this.chart.removeControl(_this.navigationControlButton);
                _this.navigationControlButton = null;
            }
        };
        _this.addMapControls = function () {
            if (!_this.navigationControlButton) {
                _this.navigationControlButton = new mapbox_gl_1["default"].NavigationControl({
                    showCompass: false
                });
                _this.chart.addControl(_this.navigationControlButton, "top-right");
            }
        };
        _this.toggleMapControls = function () {
            var isViewportFrozen = _this.isViewportFrozen();
            if (!isViewportFrozen) {
                _this.addMapControls();
            }
            else {
                _this.removeMapControls();
            }
        };
        _this.toggleInteractionEvents = function () {
            var isViewportFrozen = _this.isViewportFrozen();
            var chart = _this.chart;
            if (chart === undefined) {
                return;
            }
            var action = isViewportFrozen ? "disable" : "enable";
            geoChart_1.INTERACTION_EVENTS.forEach(function (interactionEvent) { return chart[interactionEvent][action](); });
        };
        _this.updatePanAndZoom = function () {
            _this.toggleMapControls();
            _this.toggleInteractionEvents();
        };
        _this.updateViewport = function (prevConfig) {
            ts_invariant_1["default"](_this.chart, "illegal state - updating viewport while map not initialized");
            var _a = _this.props, config = _a.config, geoData = _a.geoData;
            var data = geoData.location.data;
            var prevViewport = prevConfig.viewport;
            var viewport = config.viewport;
            if (isEqual_1["default"](prevViewport, viewport)) {
                return;
            }
            var bounds = viewport_1.getViewportOptions(data, config).bounds;
            if (bounds) {
                _this.chart.fitBounds(bounds, geoChart_1.DEFAULT_MAPBOX_OPTIONS.fitBoundsOptions);
            }
        };
        _this.setFilterMap = function () {
            ts_invariant_1["default"](_this.chart, "illegal state - setting filter while map not initialized");
            var _a = _this.props.config.selectedSegmentItems, selectedSegmentItems = _a === void 0 ? [] : _a;
            if (_this.chart.getLayer(geoChart_1.DEFAULT_LAYER_NAME)) {
                _this.chart.setFilter(geoChart_1.DEFAULT_LAYER_NAME, geoChartDataLayers_1.createPushpinFilter(selectedSegmentItems));
            }
        };
        _this.handleMapEvent = function () {
            var chart = _this.chart;
            ts_invariant_1["default"](chart, "illegal state - setting map event handlers while map not initialized");
            chart.on("click", geoChart_1.DEFAULT_LAYER_NAME, _this.handleMapClick);
            chart.on("idle", _this.handleMapIdle);
            chart.on("load", _this.setupMap);
            chart.on("load", _this.adjustChartHeight);
            chart.on("mouseenter", geoChart_1.DEFAULT_LAYER_NAME, _this.handlePushpinMouseEnter);
            chart.on("mouseleave", geoChart_1.DEFAULT_LAYER_NAME, _this.handlePushpinMouseLeave);
            chart.on("moveend", _this.handlePushpinMoveEnd);
            chart.on("zoomend", _this.handlePushpinZoomEnd);
        };
        /*
        Fired after the last frame rendered before the map enters an "idle" state:
            - No camera transitions are in progress
            - All currently requested tiles have loaded
            - All fade/transition animations have completed
        This is called one time only
        */
        _this.handleMapIdle = function () {
            var _a = _this, chart = _a.chart, afterRender = _a.props.afterRender;
            if (!chart) {
                return;
            }
            chart.off("idle", _this.handleMapIdle);
            afterRender();
        };
        _this.setupMap = function () {
            var _a = _this, chart = _a.chart, handleLayerLoaded = _a.handleLayerLoaded, props = _a.props;
            ts_invariant_1["default"](chart, "illegal state - setting up map but with no existing map instance");
            var colorStrategy = props.colorStrategy, config = props.config, geoData = props.geoData;
            var _b = config || {}, _c = _b.points, _d = (_c === void 0 ? {} : _c).groupNearbyPoints, groupNearbyPoints = _d === void 0 ? true : _d, _e = _b.showLabels, showLabels = _e === void 0 ? true : _e;
            var hasClustering = common_1.isClusteringAllowed(geoData, groupNearbyPoints);
            var dataSourceProps = {
                colorStrategy: colorStrategy,
                config: config,
                geoData: geoData,
                hasClustering: hasClustering
            };
            chart.addSource(geoChart_1.DEFAULT_DATA_SOURCE_NAME, geoChartDataSource_1.createPushpinDataSource(dataSourceProps));
            if (!hasClustering) {
                chart.addLayer(geoChartDataLayers_1.createPushpinDataLayer(geoChart_1.DEFAULT_DATA_SOURCE_NAME, geoData, config), "state-label");
            }
            else {
                chart.addLayer(geoChartDataLayers_1.createClusterPoints(geoChart_1.DEFAULT_DATA_SOURCE_NAME));
                chart.addLayer(geoChartDataLayers_1.createClusterLabels(geoChart_1.DEFAULT_DATA_SOURCE_NAME));
                // un-clustered points will be rendered under state/county label
                chart.addLayer(geoChartDataLayers_1.createUnclusterPoints(geoChart_1.DEFAULT_DATA_SOURCE_NAME), "state-label");
            }
            // that config is not public,
            // we only use for storybook to make it is more stable
            if (!showLabels) {
                var _f = chart.getStyle().layers, layers = _f === void 0 ? [] : _f;
                layers.forEach(function (layer) {
                    if (layer.id.includes(geoChart_1.LAYER_STYLE_LABEL_PREFIX)) {
                        _this.removeLayer(layer.id);
                    }
                });
            }
            // keep listening to the data event until the style is loaded
            chart.on("data", handleLayerLoaded);
        };
        _this.adjustChartHeight = function () {
            var _a, _b;
            var _c = _this, chart = _c.chart, chartRef = _c.chartRef;
            if (!chartRef || !chart) {
                return;
            }
            var chartHeight = chartRef.clientHeight;
            var parentHeight = (_b = (_a = chartRef.parentElement) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : 0;
            var shouldResize = chartHeight <= geoChart_1.ZOOM_CONTROLS_HEIGHT && geoChart_1.ZOOM_CONTROLS_HEIGHT <= parentHeight;
            if (shouldResize) {
                // set min height to re-position mapbox attribution and zoom control, in case there are too many top legend items
                // that take all visible height of widget and make geo chart container's height zero
                chartRef.style.minHeight = parentHeight + "px";
                chart.resize();
            }
        };
        _this.handleLayerLoaded = function () {
            var chart = _this.chart;
            if (!chart || !chart.isStyleLoaded()) {
                return;
            }
            chart.off("data", _this.handleLayerLoaded);
        };
        _this.createTooltip = function () {
            _this.tooltip = new mapbox_gl_1["default"].Popup(geoChart_1.DEFAULT_TOOLTIP_OPTIONS);
        };
        _this.cleanupMap = function () {
            if (!_this.chart) {
                return;
            }
            _this.removeLayer(geoChart_1.DEFAULT_LAYER_NAME);
            _this.removeLayer(geoChart_1.DEFAULT_CLUSTER_LAYER_NAME);
            _this.removeLayer(geoChart_1.DEFAULT_CLUSTER_LABELS_CONFIG.id);
            if (_this.chart.getSource(geoChart_1.DEFAULT_DATA_SOURCE_NAME)) {
                _this.chart.removeSource(geoChart_1.DEFAULT_DATA_SOURCE_NAME);
            }
        };
        _this.removeMap = function () {
            if (!_this.chart) {
                return;
            }
            // try catch to hide the mapbox's error message
            // TypeError: Cannot read property 'off' of undefined
            // mapbox is trying to call its function after deleted
            // https://github.com/mapbox/mapbox-gl-js/blob/master/src/ui/control/navigation_control.js#L118
            try {
                _this.chart.remove();
            }
            catch (_a) {
                return;
            }
        };
        _this.handlePushpinMoveEnd = function (e) {
            var target = e.target;
            var onCenterPositionChanged = _this.props.onCenterPositionChanged;
            var _a = target.getCenter(), lng = _a.lng, lat = _a.lat;
            var center = { lng: lng, lat: lat };
            onCenterPositionChanged(center);
        };
        _this.handlePushpinZoomEnd = function (e) {
            var target = e.target;
            var onZoomChanged = _this.props.onZoomChanged;
            var zoom = target.getZoom();
            onZoomChanged(zoom);
        };
        _this.handleMapClick = function (e) {
            var _a = _this.props, viewport = _a.config.viewport, drillableItems = _a.drillableItems, drillConfig = _a.drillConfig, dataView = _a.dataView, geoData = _a.geoData;
            var features = e.features, originalEvent = e.originalEvent;
            var _b = features[0], coordinates = _b.geometry.coordinates, properties = _b.properties;
            // Disable drilling in edit/export mode
            if (viewport === null || viewport === void 0 ? void 0 : viewport.frozen) {
                return;
            }
            return drilling_1.handleGeoPushpinDrillEvent(drillableItems, drillConfig, sdk_ui_1.DataViewFacade["for"](dataView), geoData, properties, coordinates, originalEvent.target);
        };
        _this.handlePushpinMouseEnter = function (e) {
            var _a = _this, chart = _a.chart, props = _a.props, tooltip = _a.tooltip;
            var config = props.config, drillableItems = props.drillableItems, intl = props.intl;
            return geoChartTooltip_1.handlePushpinMouseEnter(e, chart, tooltip, config, drillableItems, intl);
        };
        _this.handlePushpinMouseLeave = function (e) {
            var _a = _this, chart = _a.chart, props = _a.props, tooltip = _a.tooltip;
            var config = props.config;
            return geoChartTooltip_1.handlePushpinMouseLeave(e, chart, tooltip, config);
        };
        mapbox_gl_1["default"].accessToken = props.config.mapboxToken;
        _this.navigationControlButton = null;
        _this.chartRef = null;
        return _this;
    }
    GeoChartRenderer.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, selectedSegmentItems = _a.config.selectedSegmentItems, colorStrategy = _a.colorStrategy;
        var prevConfig = prevProps.config, prevColorStrategy = prevProps.colorStrategy;
        var _b = (prevConfig || {}).selectedSegmentItems, prevSelectedSegmentItems = _b === void 0 ? [] : _b;
        if (!this.chart) {
            return;
        }
        // resize map when component is updated
        // for example: toggle legend, change position of legend
        this.chart.resize();
        // only update map when style is ready
        // work around for ticket SD-898
        // avoid refresh whole map will be fixed in ticket SD-899
        if (!this.chart.isStyleLoaded()) {
            return;
        }
        var isColorChanged = common_1.isColorAssignmentItemChanged(prevColorStrategy.getColorAssignment(), colorStrategy.getColorAssignment());
        var selectedSegmentItemsChanged = selectedSegmentItems && !isEqual_1["default"](selectedSegmentItems, prevSelectedSegmentItems);
        if (!isColorChanged && selectedSegmentItemsChanged) {
            return this.setFilterMap();
        }
        this.updateMapWithConfig(prevConfig, prevColorStrategy);
    };
    GeoChartRenderer.prototype.componentDidMount = function () {
        this.createTooltip();
        this.createMap();
        this.createMapControls();
        this.handleMapEvent();
    };
    GeoChartRenderer.prototype.componentWillUnmount = function () {
        this.removeMap();
    };
    GeoChartRenderer.prototype.render = function () {
        var _a = this.props.config.isExportMode, isExportMode = _a === void 0 ? false : _a;
        var classNames = classnames_1["default"]("s-gd-geo-chart-renderer", "mapbox-container", {
            isExportMode: isExportMode,
            "s-isExportMode": isExportMode
        });
        return react_1["default"].createElement("div", { className: classNames, ref: this.setChartRef });
    };
    GeoChartRenderer.prototype.createMapControls = function () {
        ts_invariant_1["default"](this.chart, "illegal state - creating map controls while map not initialized");
        var isViewportFrozen = this.isViewportFrozen();
        this.chart.addControl(new mapbox_gl_1["default"].AttributionControl({
            compact: true
        }));
        if (!isViewportFrozen) {
            this.addMapControls();
        }
    };
    GeoChartRenderer.prototype.removeLayer = function (layerName) {
        if (!this.chart) {
            return;
        }
        if (this.chart.getLayer(layerName)) {
            this.chart.removeLayer(layerName);
        }
    };
    GeoChartRenderer.defaultProps = {
        config: {
            mapboxToken: ""
        },
        afterRender: noop_1["default"],
        onZoomChanged: noop_1["default"],
        onCenterPositionChanged: noop_1["default"]
    };
    return GeoChartRenderer;
}(react_1["default"].Component));
exports["default"] = GeoChartRenderer;
//# sourceMappingURL=GeoChartRenderer.js.map